import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { AdminModal, AdminSelect, MultipleMediaUpload } from '@project/components';
import { Form, Alert, Tabs, Tab, Button } from 'react-bootstrap';
import { RootContext } from '../../hoc/RootContext/RootContext';
import axios from '../../utils/axios';
import styles from './AssetModal.module.css';
import { v4 as uuidv4 } from 'uuid';
import AssetUpload from '../../components/AssetUpload/AssetUpload';
import AssetInput from '../AssetInput/AssetInput';
import { ASSET_SECTION_LABELS } from '../../constants';
import { useIntl } from 'react-intl';
import { useAssetModal } from './hooks'

export default function AssetModal({
  showModal,
  setShowModal,
  assetSection,
  assetData,
  spaceProfile,
  setCurrentAsset,
  assets,
  setAssets,
  isReadOnly,
  fieldSettings
}) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({id});

  const [errors, setErrors] = useState(null);
  const formRef = useRef(null);

  const assetSchemaKey = assetSection.toLowerCase().split(' ').join('_');
  const [tabKey, setTabKey] = useState('details');
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState(assetTypes[0]);
  const [assetValues, setAssetValues] = useState(assetData);
  const [selectedUnitNum, setSelectedUnitNum] = useState();
  const [unitOptions, setUnitOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState(null);
  const [uploadedFloorplan, setUploadedFloorplan] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const { assetSchema: schema, sectionSchema } = useContext(RootContext);

  const sectionInfo = sectionSchema[assetSchemaKey] || {};
  const sectionSettings = fieldSettings?.find(({section}) => section === assetSchemaKey)

  // handles change of asset section (mechanical, gas, etc)
  useAssetModal(assetSchemaKey, showModal, assetData, schema, sectionSettings, setAssetTypes, setSelectedAssetType)

  const capitalize = (str) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`; // move this to utils perhaps

  const assetCount = assets.filter(asset => asset.section === assetSchemaKey).length;

  // sets the values to be displayed by the modal inputs (defaults, or existing data if in edit mode)
  const setDefaultValues = useCallback(() => {
    if (assetData) {
      setAssetValues(assetData);
    } else {
      setAssetValues({
        // TODO: maybe add guid here
        space_profile_guid: spaceProfile.guid,
        company: spaceProfile.company,
        property: spaceProfile.property,
        section: assetSchemaKey,
        attributes: selectedAssetType.fields
          .filter(_ => _.type !== 'label')
          .reduce((prev, curr) => {
            prev[curr.id] = curr.defaultValue || (curr.type === 'checkbox' ? false : '');
            if (curr.hasOverrideToggle) prev[`${curr.id}_override`] = false;
            return prev;
          }, {})
      });
    }
  }, [selectedAssetType, assetData, spaceProfile, assetSchemaKey]);

  // build the unit # dropdown options
  useEffect(() => {
    const unitsTaken = selectedAssetType ? assets.filter(_ => _.type === selectedAssetType.type).map(_ => _.unit) : [];
    const unitOptions = [...Array(100)]
      .map((_, index) => ({ label: index + 1, value: index + 1 }))
      .filter(({ value }) => (assetData && assetData.unit === value) || !unitsTaken.includes(value));
    setSelectedUnitNum(assetData && assetData.unit ? { label: assetData.unit, value: assetData.unit } : unitOptions[0]);
    setUnitOptions(unitOptions);
  }, [assetData, assetSchemaKey, selectedAssetType, assetCount, assets]);

  // do some cleanup when switching to new type, edit, etc.
  useEffect(() => {
    if (selectedAssetType) setDefaultValues();
    setErrors(null);
  }, [assetData, selectedAssetType, setDefaultValues]);

  // if state doesn't yet have a defaul asset type to show a form for, then return null until there is one
  if (!selectedAssetType) return null;

  // creates a multidimensional array so that the form can generate its rows and columns properly
  const rowStructuredFields = selectedAssetType.fields.reduce((prev, curr) => {
    let cols = curr.cols || 12;
    let arr = prev.arr[prev.arr.length - 1];
    if (cols + prev.colTotal <= 12) {
      // still room on this row, add the field to the current row
      arr.push(curr);
      prev.colTotal += cols;
    } else {
      // we exceed 12 columns, start a new row, and add the field to it
      prev.arr.push([curr]);
      prev.colTotal = cols;
    }
    return prev;
  }, { arr: [[]], colTotal: 0 }).arr;

  const handleValidation = () => {
    const { attributes } = assetValues;
    return selectedAssetType.fields.reduce((errors, fieldSchema) => {
      if (fieldSchema.visible === false) return errors;

      const value = attributes[fieldSchema.id];
      const fieldName = fieldSchema.name || capitalize(fieldSchema.id);

      const isOverridden = fieldSchema.hasOverrideToggle && !!attributes[`${fieldSchema.id}_override`];
      const isRequired = !isOverridden && (fieldSchema.required === undefined || fieldSchema.required);
      const isBlank = value === '' || value === null || value === undefined;
      if (isBlank && isRequired && (fieldSchema.type !== 'checkbox') && (fieldSchema.type !== 'label') && (fieldSchema.id !== 'coalesce_not_present_assets')) {
        errors[fieldSchema.id] = intl.formatMessage({id: 'error.required'}, {field: fieldName});
        return errors;
      }

      if (fieldSchema.validation && typeof fieldSchema.validation === 'function' && !fieldSchema.validation(value)) {
        errors[fieldSchema.id] = intl.formatMessage({id: "error.valid"}, {field: fieldName});
      }

      return errors;
    }, {});
  }

  // saves the asset
  const handleSave = async (skipClose, newData) => {
    const isNew = !!!assetValues.guid;

    setErrors(null);
    setIsSaving(true);
    setTabKey('details');

    const errors = handleValidation();

    if (Object.keys(errors).length) {
      setErrors(errors);
      setIsSaving(false);
      return;
    }

    if (isNew) assetValues.guid = uuidv4();

    let asset = {
      ...assetValues,
      ...newData,
      type: selectedAssetType.value
    };

    // only set the unit # if this asset type uses them
    if (!selectedAssetType.hideUnit) asset.unit = selectedUnitNum.value;

    const data = new FormData();
    if (uploadedPhotos) uploadedPhotos.forEach(file => data.append('photos', file));
    if (uploadedFloorplan) data.append('floorplan', uploadedFloorplan);
    Object.keys(uploadedFiles).forEach(key => data.append(key, uploadedFiles[key]));
    data.append('asset', JSON.stringify(asset));
    data.append('assetGuid', asset.guid);

    try {
      let result = isNew ? await axios.post(`/space-profile-assets`, data) : await axios.put(`/space-profile-assets/${asset.guid}`, data);
      if (isNew) {
        assets.push(result.data.record);
      } else {
        assets[assets.findIndex(_ => _.guid === asset.guid)] = result.data.record;
      }
      setAssets([...assets]);
      setUploadedPhotos(null);
    } catch (e) {
      if (isNew) delete assetValues.guid;
      return setErrors({ generic: `${t('error.general')}: ${e.errors || e}` });
    } finally {
      setIsSaving(false);
    }
    if (!skipClose) handleClose(true);
  };

  // handles input change on asset form
  const onChange = (id, value) => {
    setIsDirty(true);
    setAssetValues(prev => ({ ...prev, attributes: { ...prev.attributes, [id]: value } }))
  };

  // handles close event on modal
  const handleClose = bypassConfirm => (isDirty || !!uploadedPhotos) && !bypassConfirm ? setShowExitConfirm(true) : setShowModal(false);

  // renders footer used for close confirmation
  const renderConfirmationFooter = () =>
    <>
      <Button variant="secondary" onClick={() => setShowExitConfirm(false)} className='btn-gray'>{t('ui.goBack')}</Button>
      <Button variant="primary" onClick={() => setShowModal(false)} className='btn-prime'>{t('ui.close')}</Button>
    </>

  // handles resetting contents after closing the modal
  const handleAfterClose = () => {
    setDefaultValues();
    setSelectedAssetType(null);
    setErrors(null);
    setAssetValues(null);
    setCurrentAsset(null);
    setUploadedPhotos(null);
    setUploadedFloorplan(null);
    setShowExitConfirm(false);
    setIsDirty(false);
    setUploadedFiles({});
    setTabKey('details');
  };

  const onDeletePhoto = (photo) => {
    if (!assetValues.photos) return
    const photos = assetValues.photos.filter(_ => _ !== photo);
    setAssetValues({
      ...assetValues,
      photos
    });
    handleSave(true, { photos: [...assetValues.photos.filter(_ => _ !== photo)] });
  }

  const onDeleteFileUpload = (fileKey) => {
    assetValues[fileKey] = null;
    setAssetValues({
      ...assetValues
    });
    handleSave(true);
  }

  const onDeleteAsset = assetData ? async () => {
    setErrors(null);
    setIsSaving(true);

    try {
      await axios.delete(`/space-profile/${assetValues.space_profile_guid}/space-profile-assets/${assetValues.guid}`);
      // remove the asset from the main list
      const index = assets.findIndex(_ => _.guid === assetValues.guid);
      if (index !== -1) {
        assets.splice(index, 1);
        setAssets([...assets]);
      }
      handleClose(true);
    } catch (e) {
      return setErrors({ generic: `${t('error.general')}: ${e.errors}` });
    } finally {
      setIsSaving(false);
    }
  } : null;

  // build a label for display
  const renderLabel = ({ label, style }) => {
    if (!style) style = 'medium'
    return <div className={styles[`modal-label-${style}`]}>{label}</div>
  }

  if (!assetValues) return null;

  return (
    <AdminModal
      title={ASSET_SECTION_LABELS[assetSection]}
      show={showModal}
      onExited={handleAfterClose}
      onHide={() => handleClose()}
      onSave={() => handleSave()}
      isSaving={isSaving}
      onDelete={!isReadOnly && onDeleteAsset}
      readOnly={isReadOnly}
      deleteMessage={t('AssetModal.deleteMessage')}
      footer={showExitConfirm ? renderConfirmationFooter() : null}>
      {showExitConfirm ? <h4>{t('AssetModal.warning')}</h4> :
        <Form ref={formRef} className="is-common-form">
          <Tabs id="asset-modal-tabs" activeKey={tabKey} onSelect={setTabKey}>
            <Tab eventKey="details" title={t('ui.details')}>
              <div>
                <div className="row my-3">
                  {!sectionInfo.singleAssetType ?
                    <div className="col-sm-6">
                      <AdminSelect
                        label={t('term.assetType')}
                        options={assetTypes}
                        value={selectedAssetType}
                        onChange={setSelectedAssetType}
                        required={true}
                        isDisabled={!!assetData || isReadOnly}
                        field={{placeholder: `${t('ui.select')}...`}}
                        identifierClass={'data-asset-type'} />
                    </div>
                    : null}
                  {!selectedAssetType.hideUnit ?
                    <div className="col-sm-6">
                      <AdminSelect
                        label={intl.formatMessage({id: 'ui.unitNo'}, {unit: selectedAssetType.unitText || t('ui.reference')})}
                        options={unitOptions}
                        value={selectedUnitNum}
                        onChange={setSelectedUnitNum}
                        required={true} isDisabled={isReadOnly} field={{placeholder: `${t('ui.select')}...`}} />
                    </div>
                    : null}
                </div>
                {assetSchemaKey === "room" ?
                  <AssetUpload
                    fileKey="floorplan"
                    description={t('term.floorplan')}
                    currentFile={assetValues.floorplan}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    readOnly={isReadOnly}
                    onDeleteFileUpload={onDeleteFileUpload} />
                  : null
                }
                {
                  rowStructuredFields
                    .filter((row) => row[0]?.id !== 'coalesce_not_present_assets')
                    .map((row, index) => 
                      <div key={'row' + index} className={`row my-3 ${row[0].visible === false ? 'd-none' : ''}`}>
                        {
                          row.map(field =>
                            <div key={field.id} className={`col-sm-${field.cols || 12}`}>
                              {
                                field.type === 'label' ?
                                  renderLabel(field) :
                                  <AssetInput
                                    field={field}
                                    capitalize={capitalize}
                                    errors={errors}
                                    assetValues={assetValues}
                                    onChange={onChange}
                                    isReadOnly={isReadOnly}
                                    isHidden={field.visible === false} />
                              }
                            </div>
                          )
                        }
                      </div>)
                }
                {
                  errors ? <Alert key={errors} variant='danger'>{t('error')}: {Object.keys(errors).map((key, i) => <div key={i}>{errors[key]}</div>)}</Alert> : null}
              </div>
            </Tab>
            <Tab eventKey="photos" title={t('ui.photos')}>
              <MultipleMediaUpload
                className={styles.assetPhotoUpload}
                mediaUrls={assetValues.photos}
                onDeleteMedia={onDeletePhoto}
                onSelectedMediaFiles={setUploadedPhotos}
                readOnly={isReadOnly}
                type="image"
              />
            </Tab>
            {
              selectedAssetType.uploads ?
                <Tab eventKey="uploads" title={t('ui.uploads')}>
                  <AssetUploadTab selectedAssetType={selectedAssetType} isReadOnly={isReadOnly} uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles} assetValues={assetValues} onDeleteFileUpload={onDeleteFileUpload} />
                </Tab>
                : null
            }
          </Tabs>
        </Form>
      }
    </AdminModal>
  )
}

// TODO: These are placeholders for future containers elsewhere
const AssetUploadTab = (props) => {
  return (
    <div>
      <div className="row my-3">
        <div className="col-sm-12">
          {
            props.selectedAssetType.uploads.map(({ id, description, icon, accept }) => <AssetUpload key={`upload_${id}`} {...props} fileKey={id} description={description} icon={icon} readOnly={props.isReadOnly} accept={accept} currentFile={props.assetValues[id]} />)
          }
        </div>
      </div>
    </div>
  );
};